import { useCallback, useEffect, useState } from 'react'
import { useAppDispatch } from '../../hooks'
import { setMyBookings, setMyInvites } from '../../stores/RoomBookingStore'
import { parseCloudRun } from '../../services/parseCloud'
import { readMyUpcomingBookings } from '../../services/myBookingsRead'
import type { RoomBookingData } from '../../../../types/RoomBooking'

export type MeetingsView = 'upcoming' | 'today' | 'past'

// parseCloudRun lets a company-closed refusal (SC-409) re-check access, so the disabled screen
// appears. Session handling stays as it was before: no sign-out here on an expired token.
const CLOUD_OPTS = { handleInvalidSession: false }

/**
 * The two lists behind the Meetings page and the in-room My Bookings panel, into the store: the
 * user's meetings for the chosen view (`myBookings`), and their upcoming invites, declined ones
 * included (`myInvites`). Each is one getRoomBookings call (SC-477).
 *
 * The invites don't depend on the view, so switching it reloads only the meetings. The Upcoming
 * view is the dashboard reminders' own read, so on the dashboard the two share it.
 *
 * @param enabled Reads only while true (the in-room panel is open).
 * @param refreshTrigger The store's `bookingsRefreshTrigger`: a booking change reloads both lists.
 * @returns Whether a read is in flight.
 */
export function useMyBookingLists(
  companyId: string,
  enabled: boolean,
  view: MeetingsView,
  refreshTrigger: number
): boolean {
  const dispatch = useAppDispatch()
  const [readsInFlight, setReadsInFlight] = useState(0)

  const track = useCallback(async (read: () => Promise<void>) => {
    setReadsInFlight((n) => n + 1)
    try {
      await read()
    } catch (err) {
      console.warn('[useMyBookingLists] fetch failed:', err)
    } finally {
      setReadsInFlight((n) => n - 1)
    }
  }, [])

  useEffect(() => {
    if (!enabled || !companyId) return
    void track(async () => {
      if (view === 'upcoming') {
        dispatch(setMyBookings(await readMyUpcomingBookings(companyId, refreshTrigger)))
        return
      }
      const now = new Date()
      const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString()
      const dayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toISOString()
      const res = await parseCloudRun<{ bookings?: RoomBookingData[] }>(
        'getRoomBookings',
        { companyId, dateFilter: view, dayStart, dayEnd, creatorOrInvitee: true },
        CLOUD_OPTS
      )
      dispatch(setMyBookings(res?.bookings || []))
    })
  }, [enabled, companyId, view, refreshTrigger, dispatch, track])

  useEffect(() => {
    if (!enabled || !companyId) return
    void track(async () => {
      const res = await parseCloudRun<{ bookings?: RoomBookingData[] }>(
        'getRoomBookings',
        { companyId, inviteeUserId: 'me' },
        CLOUD_OPTS
      )
      dispatch(setMyInvites(res?.bookings || []))
    })
  }, [enabled, companyId, refreshTrigger, dispatch, track])

  return readsInFlight > 0
}
